import { useCallback, useState } from 'react';
import {
	getThemeConfig as getSpeecargoThemeConfig,
	getThemeSettings as getSpeecargoThemeSettings,
	THEME_STORAGE_KEY,
	type ThemeMode,
	type ThemeSettings,
} from '@/theme/SpeecargoTheme';

export type { ThemeMode, ThemeSettings };

export default function useThemeModel() {
	const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
		try {
			const saved = localStorage.getItem(THEME_STORAGE_KEY);
			return (saved as ThemeMode) || 'speedcargo';
		} catch {
			return 'speedcargo';
		}
	});

	const toggleTheme = useCallback(() => {
		// Cycle through themes: speedcargo -> twa -> panam -> light -> dark -> speedcargo
		const themeOrder: ThemeMode[] = [
			'speedcargo',
			'twa',
			'panam',
			'light',
			'dark',
		];
		const currentIndex = themeOrder.indexOf(themeMode);
		const nextIndex = (currentIndex + 1) % themeOrder.length;
		const newMode = themeOrder[nextIndex];

		setThemeMode(newMode);
		try {
			localStorage.setItem(THEME_STORAGE_KEY, newMode);
		} catch (error) {
			console.warn('Failed to save theme preference:', error);
		}
	}, [themeMode]);

	const setTheme = useCallback((mode: ThemeMode) => {
		setThemeMode(mode);
		try {
			localStorage.setItem(THEME_STORAGE_KEY, mode);
		} catch (error) {
			console.warn('Failed to save theme preference:', error);
		}
	}, []);

	const getThemeSettings = useCallback((): ThemeSettings => {
		return getSpeecargoThemeSettings(themeMode);
	}, [themeMode]);

	const getThemeConfig = useCallback(() => {
		return getSpeecargoThemeConfig(themeMode);
	}, [themeMode]);

	return {
		themeMode,
		toggleTheme,
		setTheme,
		getThemeSettings,
		getThemeConfig,
	};
}
